import marketplaceCountryCodesProd from './generated/marketplaceCountryCodes.prod.json';
import marketplaceCountryCodesDevo from './generated/marketplaceCountryCodes.devo.json';

/*
 * Resolves a marketplace's default country code.
 *
 * This used to construct an ACS OfflineConfigManager client at module scope and
 * query it per call. That pulled the offline adapter and an Ion parser into the
 * entry bundle -- ~700 KiB of code plus ~484 KiB of JSON, both stages of it, to
 * serve a lookup returning one string. The documents are Ion-encoded and carry
 * no marketplace id, so queries.json had to be joined to them on sequenceId at
 * runtime; that join is what required the parser.
 *
 * scripts/generateMarketplaceCountryCodes.js now performs that join at install
 * time and emits the result as a plain map, so the browser needs neither
 * dependency. Verified against the real client across every marketplace in both
 * datasets: identical answers, including '' for ids it does not know.
 *
 * The generated maps are produced by `postinstall`, immediately after
 * scripts/acsPostinstall.sh copies the configuration out of the
 * MarketplaceOfflineConfig Brazil package, so they cannot drift from the data
 * they were derived from. They are gitignored for the same reason the copied
 * configuration is not committed.
 */

/*
 * Both stages are kept because the datasets are NOT interchangeable. Measured:
 * 355 marketplaces appear only in prod, 231 only in devo, 12 in both, and of
 * those 12 one disagrees -- A1D38IXLEJ6VQC is DE in prod and IN in devo.
 * Collapsing to a single map would therefore change what beta returns. At 9.2
 * and 6.1 KiB it is cheaper to keep both than to reason about the difference.
 */
const isProd =
  window.location.hostname.includes('advertising.amazon') ||
  window.location.hostname.includes('advertising-japan.amazon');
const isGamma = window.location.hostname.includes('preprod');
const isNotBeta = isProd || isGamma;

const countryCodeByMarketplaceId: ReadonlyMap<string, string> = new Map(
  // A Map rather than the imported object: a plain object lookup resolves
  // inherited keys, so getCountryCodeByMarketplace('__proto__') returned
  // Object.prototype -- truthy, so the '' fallback below did not catch it, and
  // callers typed it as a string. Object.entries takes only own enumerable
  // properties, and Map.get has no prototype chain to fall through to.
  Object.entries(
    isNotBeta ? marketplaceCountryCodesProd : marketplaceCountryCodesDevo,
  ),
);

/**
 * Returns the marketplace's default country code, or '' when the marketplace is
 * unknown. The empty-string fallback matches the previous client behaviour and
 * callers rely on it.
 */
export const getCountryCodeByMarketplace = (marketplaceId: string): string => {
  return countryCodeByMarketplaceId.get(marketplaceId) ?? '';
};
